import { Link } from "react-router-dom";
import { Heart, Navigation } from "lucide-react";
import { toast } from "sonner";
import { money, miles } from "@/lib/format";
import { useFavorites } from "@/hooks/useFavorites";
import { SampleBadge } from "@/components/common/ui";

const Facts = ({ l }) => (
  <p className="mt-1 flex flex-wrap items-center text-[13px] text-ink">
    {[`${l.beds} beds`, `${l.baths} baths`, `${l.sqft?.toLocaleString()} sqft`, l.lot_acres ? `${l.lot_acres} acres` : null]
      .filter(Boolean)
      .map((t, i) => (
        <span key={t} className={i ? "border-l border-neutral-300 pl-2 ml-2" : ""}>{t}</span>
      ))}
  </p>
);

export const SaveButton = ({ listingKey, className = "" }) => {
  const { isSaved, toggle } = useFavorites();
  const saved = isSaved(listingKey);
  return (
    <button
      type="button"
      aria-label={saved ? "Remove from saved" : "Save home"}
      data-testid={`save-home-${listingKey}`}
      onClick={async (e) => {
        e.preventDefault();
        e.stopPropagation();
        try {
          const res = await toggle(listingKey);
          if (res !== null) toast(res ? "Saved to your account" : "Removed from saved homes");
        } catch {
          toast.error("Couldn't update saved homes. Please try again.");
        }
      }}
      className={`grid h-9 w-9 place-items-center rounded-full bg-white/95 shadow transition-transform hover:scale-110 ${className}`}
    >
      <Heart className={`h-[18px] w-[18px] ${saved ? "fill-ink text-ink" : "text-ink"}`} />
    </button>
  );
};

/** Compass-style listing card with base-distance chip. */
export const ListingCard = ({ listing: l, baseShort, onHover, active = false }) => {
  const dist = l.base_miles != null ? { label: `${miles(l.base_miles)} to ${baseShort}` } : { label: `${miles(l.nearest_base?.miles)} to ${l.nearest_base?.short}` };
  return (
    <Link
      to={`/listing/${l.listing_key}`}
      data-testid={`listing-card-${l.listing_key}`}
      id={`card-${l.listing_key}`}
      onMouseEnter={() => onHover?.(l.listing_key)}
      onMouseLeave={() => onHover?.(null)}
      className={`group block overflow-hidden rounded-md border bg-white transition-[box-shadow,border-color] duration-200 hover:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.35)] ${
        active ? "border-ink shadow-[0_12px_32px_-14px_rgba(0,0,0,0.35)]" : "border-neutral-200"
      }`}
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-neutral-100">
        <img src={l.photos?.[0]} alt={`${l.title} in ${l.neighborhood}`} loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
        {l.sample && <SampleBadge className="absolute left-3 top-3" />}
        <SaveButton listingKey={l.listing_key} className="absolute right-3 top-3" />
        <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-2.5 py-1 text-[12px] font-medium text-ink shadow-sm" data-testid={`listing-distance-${l.listing_key}`}>
          <Navigation className="h-3 w-3 text-brass-deep" /> {dist.label}
        </span>
      </div>
      <div className="px-3.5 pb-3.5 pt-3">
        <p className="text-xl font-medium text-ink">{money(l.price)}</p>
        <Facts l={l} />
        <p className="mt-1 truncate text-[13px] text-neutral-500">
          {l.title} · {l.neighborhood}, {l.city}, {l.state} {l.postcode}
        </p>
      </div>
    </Link>
  );
};
